import { logEvent } from 'firebase/analytics';
import { analytics, auth } from '../firebase_config';

// Telegram alerting configuration
const TELEGRAM_CONFIG = {
  // Your personal Telegram user ID for alerts
  ADMIN_CHAT_ID: '1083695109', // Your Telegram user ID
  BOT_ACCOUNT: 'shelby-hoa', // OpenClaw account ID for your bot
  // Rate limiting to prevent spam
  MAX_ALERTS_PER_HOUR: 5,
  BATCH_DELAY_MS: 30000, // 30 seconds
};

class ErrorReporter {
  constructor() {
    this.analytics = analytics;
    this.isEnabled = process.env.NODE_ENV === 'production'; // Only report in production
    
    // Telegram alerting state
    this.alertHistory = new Map(); // Track recent alerts to prevent spam
    this.alertQueue = [];
    this.lastAlertTime = 0;
  }

  /**
   * Report a general error to Firebase Analytics
   * @param {Error} error - The error object
   * @param {Object} context - Additional context about the error
   */
  async reportError(error, context = {}) {
    if (!this.isEnabled) {
      console.warn('Error (dev mode):', error, context);
      return;
    }

    try {
      const errorData = {
        error_message: this.truncateString(error.message || 'Unknown error', 100),
        error_type: error.name || 'Error',
        error_stack: this.truncateString(error.stack || '', 500),
        component: context.component || 'unknown',
        action: context.action || 'unknown',
        severity: context.severity || 'error',
        user_id: auth.currentUser?.uid || 'anonymous',
        url: window.location.pathname,
        timestamp: Date.now()
      };

      // Add any additional context
      Object.keys(context).forEach(key => {
        if (!errorData[key] && typeof context[key] === 'string') {
          errorData[key] = this.truncateString(context[key], 100);
        }
      });

      logEvent(this.analytics, 'app_error', errorData);
      
      // Send Telegram alert for high-priority errors
      await this.considerTelegramAlert(error, context);
      
      console.error('Error reported to Firebase:', error, context);
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
      console.error('Original error:', error);
    }
  }

  /**
   * Report autopay-specific errors with additional context
   */
  reportAutopayError(error, context = {}) {
    return this.reportError(error, {
      component: 'autopay',
      severity: 'high',
      ...context
    });
  }

  /**
   * Report UI/component errors
   */
  reportUIError(error, component, action, context = {}) {
    return this.reportError(error, {
      component,
      action,
      severity: 'medium',
      ...context
    });
  }

  /**
   * Report API/network errors
   */
  reportAPIError(error, endpoint, method = 'GET', context = {}) {
    return this.reportError(error, {
      component: 'api',
      action: `${method} ${endpoint}`,
      severity: 'high',
      endpoint,
      method,
      ...context
    });
  }

  /**
   * Report authentication errors
   */
  reportAuthError(error, action, context = {}) {
    return this.reportError(error, {
      component: 'authentication',
      action,
      severity: 'high',
      ...context
    });
  }

  /**
   * Report payment/subscription errors
   */
  reportPaymentError(error, action, context = {}) {
    return this.reportError(error, {
      component: 'payment',
      action,
      severity: 'critical',
      ...context
    });
  }

  /**
   * Truncate strings for Firebase Analytics limits
   */
  truncateString(str, maxLength) {
    if (!str || typeof str !== 'string') return str;
    return str.length > maxLength ? str.substring(0, maxLength - 3) + '...' : str;
  }

  // =================== TELEGRAM ALERTING METHODS ===================

  /**
   * Decide whether to send a Telegram alert for this error
   */
  async considerTelegramAlert(error, context) {
    if (!this.isEnabled) return; // No alerts in development
    
    if (this.shouldAlert(error, context)) {
      await this.queueTelegramAlert(error, context);
    }
  }

  /**
   * Determine if this error warrants a Telegram alert
   */
  shouldAlert(error, context) {
    const severity = context.severity || 'medium';
    const component = context.component || 'unknown';
    
    // Always alert for critical errors
    if (severity === 'critical') return true;
    
    // Always alert for autopay issues (your main concern)
    if (component === 'autopay' || context.action?.includes('autopay')) return true;
    
    // Always alert for high severity errors
    if (severity === 'high') return true;
    
    // Alert for payment/auth errors
    if (['payment', 'authentication'].includes(component)) return true;
    
    // Alert if this is a repeated error (3+ times in last hour)
    if (this.isRepeatedError(error, context)) return true;
    
    return false;
  }

  /**
   * Check if this error has occurred repeatedly recently
   */
  isRepeatedError(error, context) {
    const errorKey = `${context.component}_${context.action}_${error.message}`.substring(0, 100);
    const now = Date.now();
    const hourAgo = now - (60 * 60 * 1000);
    
    // Clean old entries
    for (const [key, timestamps] of this.alertHistory.entries()) {
      const recentTimestamps = timestamps.filter(t => t > hourAgo);
      if (recentTimestamps.length === 0) {
        this.alertHistory.delete(key);
      } else {
        this.alertHistory.set(key, recentTimestamps);
      }
    }
    
    // Track this error
    if (!this.alertHistory.has(errorKey)) {
      this.alertHistory.set(errorKey, []);
    }
    this.alertHistory.get(errorKey).push(now);
    
    // Alert if this error has occurred 3+ times in the last hour
    return this.alertHistory.get(errorKey).length >= 3;
  }

  /**
   * Queue a Telegram alert with rate limiting
   */
  async queueTelegramAlert(error, context) {
    const now = Date.now();
    const hourAgo = now - (60 * 60 * 1000);
    
    // Clean old queue entries
    this.alertQueue = this.alertQueue.filter(alert => alert.timestamp > hourAgo);
    
    // Check rate limit
    if (this.alertQueue.length >= TELEGRAM_CONFIG.MAX_ALERTS_PER_HOUR) {
      console.warn('Telegram alert rate limit reached, skipping alert');
      return;
    }
    
    // Respect batching delay
    const timeSinceLastAlert = now - this.lastAlertTime;
    if (timeSinceLastAlert < TELEGRAM_CONFIG.BATCH_DELAY_MS) {
      console.warn('Telegram alert too soon after last alert, skipping');
      return;
    }
    
    // Queue and send the alert
    const alertData = { error, context, timestamp: now };
    this.alertQueue.push(alertData);
    
    await this.sendTelegramAlert(error, context);
    this.lastAlertTime = now;
  }

  /**
   * Send the actual Telegram alert message
   */
  async sendTelegramAlert(error, context) {
    try {
      const message = this.formatTelegramMessage(error, context);
      
      // For development/testing - just log the alert
      if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
        console.log('📢 TELEGRAM ALERT (dev mode - simulated):\n', message);
        console.log('📝 Would be sent to:', TELEGRAM_CONFIG.ADMIN_CHAT_ID);
        return;
      }
      
      // In production, use Firebase Cloud Function
      const response = await fetch('https://us-central1-shelby-estates-hoa.cloudfunctions.net/sendTelegramAlert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: message,
          severity: context.severity,
          component: context.component
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      if (result.success) {
        console.log('✅ Telegram alert sent successfully');
      } else {
        console.error('Telegram alert failed:', result.error);
      }
      
    } catch (alertError) {
      console.error('Failed to send Telegram alert:', alertError);
      // Don't throw - we don't want Telegram failures to break the app
    }
  }

  /**
   * Format error information for Telegram message
   */
  formatTelegramMessage(error, context) {
    const severity = context.severity || 'unknown';
    const emoji = {
      'critical': '🚨',
      'high': '⚠️',
      'medium': '🟡',
      'low': '📝'
    }[severity] || '❓';
    
    const timestamp = new Date().toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    let message = `${emoji} *Shelby HOA Error Alert*\n\n`;
    message += `🏠 *Component:* ${context.component || 'Unknown'}\n`;
    message += `🎯 *Action:* ${context.action || 'Unknown'}\n`;
    message += `👤 *User:* ${context.user_id || 'Anonymous'}\n`;
    message += `📱 *Error:* ${this.truncateString(error.message, 100)}\n`;
    message += `📍 *Page:* ${window.location.pathname}\n`;
    message += `🕐 *Time:* ${timestamp} CT\n`;
    
    // Add context-specific details
    if (context.customer_id) {
      message += `💳 *Customer:* ${context.customer_id}\n`;
    }
    if (context.account_id) {
      message += `🏡 *Account:* ${context.account_id}\n`;
    }
    if (context.house_number) {
      message += `🏠 *House:* ${context.house_number}\n`;
    }
    
    // Add severity context
    if (severity === 'critical') {
      message += `\n⚠️ *This is a CRITICAL error requiring immediate attention*`;
    } else if (context.component === 'autopay') {
      message += `\n💰 *Autopay system affected - may impact resident payments*`;
    }
    
    return message;
  }

  /**
   * Report user action success for tracking
   */
  reportSuccess(action, component, context = {}) {
    if (!this.isEnabled) return;

    try {
      logEvent(this.analytics, 'app_action_success', {
        action,
        component,
        user_id: auth.currentUser?.uid || 'anonymous',
        timestamp: Date.now(),
        ...context
      });
    } catch (error) {
      console.warn('Failed to report success event:', error);
    }
  }
}

// Create singleton instance
const errorReporter = new ErrorReporter();

// Global error handlers
if (typeof window !== 'undefined') {
  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    errorReporter.reportError(new Error(event.reason), {
      component: 'global',
      action: 'unhandled_promise_rejection'
    }).catch(err => {
      console.error('Failed to report unhandled promise rejection:', err);
    });
  });

  // Catch global JavaScript errors
  window.addEventListener('error', (event) => {
    errorReporter.reportError(new Error(event.message), {
      component: 'global', 
      action: 'global_error',
      filename: event.filename,
      line_number: event.lineno,
      column_number: event.colno
    }).catch(err => {
      console.error('Failed to report global error:', err);
    });
  });
}

export default errorReporter;